import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

import { ArrowRightIcon, ChevronRightIcon } from "@prequel/shared-components";

export type PipelineCardRow = {
  label: string;
  value: ReactNode;
};

export type PipelineCardItem = {
  id: string;
  title: string;
  subtitle?: ReactNode;
  to: string;
};

type PipelineCardBase = {
  id: string;
  label: string;
  icon?: ReactNode;
};

/** A card is either a single resource, or a counted list of linked resources. */
export type PipelineCard = PipelineCardBase &
  (
    | { title: string; subtitle?: ReactNode; to?: string; rows?: PipelineCardRow[]; items?: never; emptyState?: never }
    | { items: PipelineCardItem[]; emptyState?: ReactNode; title?: never; subtitle?: never; to?: never; rows?: never }
  );

const CardBody = ({ title, subtitle, linked }: { title: string; subtitle?: ReactNode; linked: boolean }) => (
  <div className="flex items-center justify-between gap-2 px-4 py-3">
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-stone-800">{title}</div>
      {subtitle && <div className="truncate text-xs text-stone-500">{subtitle}</div>}
    </div>
    {linked && <ChevronRightIcon className="shrink-0 text-stone-400" />}
  </div>
);

const CardItems = ({ items, emptyState }: { items: PipelineCardItem[]; emptyState?: ReactNode }) => {
  if (items.length === 0) {
    return <div className="px-4 py-3 text-sm text-stone-500">{emptyState}</div>;
  }

  return (
    <ul className="divide-y divide-stone-100">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            className="
              block
              hover:bg-stone-50
            "
          >
            <CardBody title={item.title} subtitle={item.subtitle} linked />
          </Link>
        </li>
      ))}
    </ul>
  );
};

// Flows with fewer cards get gutters instead of wider cards, so a card is the same width on every
// detail page. Source→Datasets reads at the same scale as Dataset→Stream→Destination.
const FULL_FLOW_CARD_COUNT = 3;

const Spacer = ({ grow }: { grow: number }) => <div aria-hidden="true" style={{ flex: `${grow} 1 0%` }} />;

const PipelineCards = ({ cards }: { cards: PipelineCard[] }) => {
  const gutterGrow = Math.max(0, FULL_FLOW_CARD_COUNT - cards.length) / 2;

  return (
    <div className="mb-4 flex items-start gap-2 rounded-md bg-stone-50 p-4">
      {gutterGrow > 0 && <Spacer grow={gutterGrow} />}
      {cards.map((card, index) => (
        <Fragment key={card.id}>
          {/* Sits level with the card headers rather than centered on the tallest card. */}
          {index > 0 && <ArrowRightIcon className="mt-2.5 shrink-0 text-stone-300" />}
          <div className="min-w-0 flex-1 rounded-md border border-stone-200 bg-white">
            <div className="flex items-center gap-1.5 border-b border-stone-100 px-4 py-2 text-sm font-medium">
              {card.icon}
              <span>{card.label}</span>
              {card.items && (
                <span className="rounded-md bg-stone-100 px-1.5 py-0.5 text-xs font-medium text-stone-600">
                  {card.items.length}
                </span>
              )}
            </div>
            {card.items ? (
              <CardItems items={card.items} emptyState={card.emptyState} />
            ) : card.to ? (
              <Link
                to={card.to}
                className="
                  block
                  hover:bg-stone-50
                "
              >
                <CardBody title={card.title} subtitle={card.subtitle} linked />
              </Link>
            ) : (
              <CardBody title={card.title} subtitle={card.subtitle} linked={false} />
            )}
            {card.rows && (
              <div className="border-t border-stone-100 px-4 py-1">
                {card.rows.map((row) => (
                  <div key={row.label} className="flex min-h-7 items-center justify-between gap-2 text-sm">
                    <span className="text-stone-600">{row.label}</span>
                    <span className="min-w-0 truncate text-stone-800">{row.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Fragment>
      ))}
      {gutterGrow > 0 && <Spacer grow={gutterGrow} />}
    </div>
  );
};

export default PipelineCards;
